import React, { useState } from 'react';
import { Button } from 'rebass';
import { FiEdit } from 'react-icons/fi';
import { useAuth } from 'context/AuthContext';
import { useToastHelper } from 'hooks/useToastHelper';
import { useUpdateAccountSettingsMutation } from 'api/useUpdateAccountSettingsMutation';
import { countryListData } from 'utils/constants';
import { AllowedCountries } from 'containers/SettingsPage/Modals/AllowedCountries';
import services from 'services/services';
import auth from 'services/auth';

/**
 * Self-contained allowed-countries subsection.
 *
 * Reads the current country list from AuthContext, renders it inline,
 * and opens the {@link AllowedCountries} picker modal for editing.
 * Saves via {@link useUpdateAccountSettingsMutation}.
 */
export function AllowedCountriesSettings() {
    const authContext = useAuth();
    const settings = authContext.account.settings;
    const [showModal, setShowModal] = useState(false);
    const { addSuccessToast, addErrorToast } = useToastHelper();

    const { mutate } = useUpdateAccountSettingsMutation({
        onSuccess: () => {
            auth.checkAuth();
            addSuccessToast('Saved');
        },
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error) || 'Something went wrong. Please try again later.';
            addErrorToast(errorText);
        }
    });

    const handleSave = countryList => {
        const allowAll = !countryList.length;
        authContext.account.settings.allowed_countries = countryList;
        authContext.account.settings.allow_all_countries = allowAll;
        mutate({ allowed_countries: countryList, allow_all_countries: allowAll });
    };

    const renderCountries = () => {
        if (settings && settings.allow_all_countries == 1) {
            return <div className="text-secondary-dark">Allow all countries</div>;
        }
        if (settings.allowed_countries && settings.allowed_countries.length > 0) {
            const labels = settings.allowed_countries
                .map(value => {
                    const match = countryListData.find(item => item.value === value);
                    return match ? match.label : null;
                })
                .filter(Boolean);
            return labels.join(', ');
        }
        return null;
    };

    return (
        <div id="country-restrictions">
            <h3 className="fs-title-16 mb-2 flex items-center gap-2">
                Allowed Countries
                <a
                    href="https://help.dscout.com/hc/en-us/articles/37035117943188"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="help-question"
                >
                    ?
                </a>
            </h3>
            <p id="countries-description" className="form-section-information mb-3">
                Allow access to respondents from specific countries.
            </p>
            <div className="form-data flex items-center">
                <div>{renderCountries()}</div>
                <Button
                    className="va-top"
                    ml={2}
                    mt={'-4px'}
                    variant="link"
                    type="button"
                    aria-label="Edit allowed countries"
                    onClick={() => setShowModal(true)}
                >
                    <FiEdit />
                </Button>
            </div>
            <AllowedCountries
                isOpen={showModal}
                onRequestClose={() => setShowModal(false)}
                onSave={handleSave}
                selectedCountries={settings.allowed_countries}
            />
        </div>
    );
}
